import {
  Observable,
  FetchResult,
  ObservableSubscription,
} from '@apollo/client';

type ReconnectingSubscriptionOptions<T = any> = {
  onData?: (
    data: FetchResult<T, Record<string, any>, Record<string, any>>,
  ) => void;
  onError?: (error: any) => void;
  observe: () => Observable<
    FetchResult<T, Record<string, any>, Record<string, any>>
  >;
};

export class ReconnectingSubscription<T> {
  private onData: ReconnectingSubscriptionOptions<T>['onData'];
  private onError: ReconnectingSubscriptionOptions<T>['onError'];
  private observe: ReconnectingSubscriptionOptions<T>['observe'];
  private subscription?: ObservableSubscription;
  public hasSubscription = false;

  constructor(options: ReconnectingSubscriptionOptions<T>) {
    this.onData = options.onData;
    this.onError = options.onError;
    this.observe = options.observe;
  }

  public subscribe() {
    this.subscription = this.observe().subscribe({
      next: (fetchResult) => this.handleData(fetchResult),
      error: (...args) => this.handleError(...args),
    });
    this.hasSubscription = true;
  }

  public unsubscribe() {
    this.hasSubscription = false;

    // We force unsubscribe to wait for 2s due to a bug with socket reuse
    // https://github.com/awslabs/aws-mobile-appsync-sdk-js/issues/491
    setTimeout(() => {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
    }, 2000);
  }

  private handleData(
    fetchResult: FetchResult<T, Record<string, any>, Record<string, any>>,
  ) {
    if (this.onData) {
      this.onData(fetchResult);
    }
  }

  private handleError(error: any) {
    console.error(error);
    if (
      error.errorMessage &&
      (/Socket closed/g.test(error.errorMessage) ||
        /Ping timed out/g.test(error.errorMessage))
    ) {
      this.unsubscribe();
      this.subscribe();
    }
    if (this.onError) {
      this.onError(error);
    }
  }
}
